import { type ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './AppFrame.module.css';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

interface AppFrameProps {
  children: ReactNode;
  topbarRight?: ReactNode;
}

export function AppFrame({ children, topbarRight }: AppFrameProps) {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // Auto-close the mobile drawer after every navigation so users don't get
  // stranded on the destination page with the drawer still over the content.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // ESC closes the drawer — matches Dialog/CommandPalette behavior.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  return (
    <div className="bv">
      <div className={`${styles.app} ${navOpen ? styles.navOpen : ''}`}>
        <Topbar right={topbarRight} onMenuClick={() => setNavOpen((v) => !v)} />
        <Sidebar />
        {navOpen && (
          <button
            type="button"
            className={styles.backdrop}
            aria-label="Sluit menu"
            onClick={() => setNavOpen(false)}
          />
        )}
        {/* tabIndex=0 makes the scroll container keyboard-focusable so users
            who Tab past the sidebar can arrow-key through screen content. axe
            requires this on scrollable regions without inline focusable kids
            (notably /garanties). Biome's noNoninteractiveTabindex doesn't
            account for the WCAG scrollable-region exception. */}
        {/* biome-ignore lint/a11y/noNoninteractiveTabindex: see WCAG 2.1.1 + axe scrollable-region-focusable */}
        <main className={styles.main} id="main" tabIndex={0}>
          {children}
        </main>
      </div>
    </div>
  );
}
